import type { PdfFormDefinition, RpcFn } from "./types";
import { applyJoinFields, parseDMY } from "./utils";
import type { CommandType, CommandPayload } from "../../../shared/src/commands";

export interface ExistingPatient {
  id: string;
  name: string | null;
  dateOfBirth: string | null;
  bloodType: string | null;
}

export interface ExistingCase {
  id: string;
  patientId: string;
  eventTime: number | null;
}

export interface ImportCaseResult {
  caseUuid: string;
  skipped: boolean;
}

/**
 * Build and dispatch all commands to import one PDF case.
 *
 * Deduplication — layer 1 (patient):
 *   Matches by name + DOB. A blood type conflict (both sides known, non-unknown,
 *   and different) breaks the match and creates a new patient instead.
 *
 * Deduplication — layer 2 (case):
 *   If an existing patient was matched, checks for a case with the same event
 *   timestamp. Returns { skipped: true, caseUuid: <existing id> } if found.
 */
export async function importCase(
  fields: Record<string, string | boolean>,
  form: PdfFormDefinition,
  existingPatients: ExistingPatient[],
  existingCases: ExistingCase[],
  options: { unitUuid: string; hospitalUuid?: string },
  bulkDispatch: (cmds: { type: CommandType; payload: CommandPayload }[]) => Promise<void>,
  rpc: RpcFn,
  createUuid: () => string,
): Promise<ImportCaseResult> {
  const resolved = applyJoinFields(fields, form.joinFields);

  // ── Extract dedup fields from PDF ─────────────────────────────────────
  const pdfName = ((resolved.patientName as string) || "").trim().toLowerCase();
  const pdfDobRaw = (resolved.dateOfBirth as string) || "";
  let pdfDob: string | null = null;
  if (pdfDobRaw) {
    const ts = parseDMY(pdfDobRaw);
    if (ts !== null) {
      const d = new Date(ts);
      pdfDob = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    }
  }
  const pdfBloodType =
    ((resolved["p1-blood-type"] as string) || (resolved["p2-blood-type"] as string) || "").trim() || null;
  const pdfEventTs = parseDMY((resolved.eventDate as string) || "");

  // ── Layer 1: Patient deduplication ───────────────────────────────────
  let patientUuid = createUuid();
  let existingPatient = false;

  if (pdfName && pdfDob) {
    const match = existingPatients.find((p) => {
      if (!p.name || !p.dateOfBirth) return false;
      if (p.name.trim().toLowerCase() !== pdfName) return false;
      if (p.dateOfBirth !== pdfDob) return false;
      // Blood type conflict → no match
      if (
        pdfBloodType && p.bloodType &&
        pdfBloodType !== "unknown" && p.bloodType !== "unknown" &&
        pdfBloodType !== p.bloodType
      ) return false;
      return true;
    });
    if (match) {
      patientUuid = match.id;
      existingPatient = true;
    }
  }

  // ── Layer 2: Case deduplication ───────────────────────────────────────
  if (existingPatient && pdfEventTs !== null) {
    const dup = existingCases.find(
      (c) => c.patientId === patientUuid && c.eventTime === pdfEventTs,
    );
    if (dup) return { caseUuid: dup.id, skipped: true };
  }

  // ── Build and dispatch commands ───────────────────────────────────────
  const ids = { patientUuid, caseUuid: createUuid() };
  const cmds: { type: CommandType; payload: CommandPayload }[] = [];
  const collect = async (type: string, payload: Record<string, unknown>) => {
    cmds.push({ type: type as CommandType, payload: payload as unknown as CommandPayload });
    return { ack: Promise.resolve() as Promise<void> };
  };

  if (!existingPatient) collect("patient.create", { uuid: ids.patientUuid, unitUuid: options.unitUuid });
  collect("case.create", { uuid: ids.caseUuid });
  collect("case.setPatient", { uuid: ids.caseUuid, patientUuid: ids.patientUuid });
  collect("case.setEventTime", { uuid: ids.caseUuid, timestamp: Date.now() });
  if (options.hospitalUuid) collect("case.setHospital", { uuid: ids.caseUuid, hospitalUuid: options.hospitalUuid });
  await form.importAll(fields, ids, collect, rpc, createUuid);
  await bulkDispatch(cmds);

  return { caseUuid: ids.caseUuid, skipped: false };
}
